<template>
  <v-container class="text-center" fluid style="width: 80%">
    <template v-if="loading">
      <v-row justify="center" align="center" style="height: 300px">
        <v-col cols="12" class="d-flex flex-column align-center justify-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="80"
            width="8"
          ></v-progress-circular>
          <div class="mt-4" style="font-size: 1.5rem; color: #1976d2">
            Loading, please wait...
          </div>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <!-- Total Files Card -->
      <v-card class="mb-4" color="primary" dark>
        <v-card-title class="text-h5" style="width: 100%">
          <div
            class="d-flex flex-column flex-sm-row align-center justify-center justify-sm-space-between"
            style="width: 100%"
          >
            <span class="mb-2 mb-sm-0">Total Files: {{ totalCount }}</span>
            <div class="d-flex justify-center" style="gap: 22px">
              <v-chip small color="success" class="white--text">
                <v-icon left small>mdi-play-circle-outline</v-icon>
                {{ activeCount }} Active
              </v-chip>
              <v-chip small color="grey darken-1" class="white--text">
                <v-icon left small>mdi-archive-outline</v-icon>
                {{ archivedCount }} Archived
              </v-chip>
            </div>
          </div>
        </v-card-title>
      </v-card>

      <v-simple-table dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-center">DESCRIPTION</th>
              <th class="text-center">NUMBER OF FILES</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="stage in stages"
              :key="stage.id"
              :style="{
                cursor: countForStage(stage.stageCode)
                  ? 'pointer'
                  : 'not-allowed',
                opacity: countForStage(stage.stageCode) ? 1 : 0.5,
              }"
              @click="
                countForStage(stage.stageCode) &&
                  viewItem(stage.stageCode, $route.params.id)
              "
            >
              <td>
                {{ stage.name }}
              </td>
              <td v-if="countForStage(stage.stageCode)">
                {{ countForStage(stage.stageCode) }}
              </td>
              <td v-else>0</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </template>
  </v-container>
</template>

<script>
import CasefileDataService from "../../services/CasefileDataService";
import StageDataService from "../../services/StageDataService";
// import DeptDataService from "../../services/DeptDataService";

export default {
  name: "brTabDetail",
  components: {},

  data() {
    return {
      casefiles: [],
      countsByStatus: {},
      stages: [],
      totalCount: 0,
      activeCount: 0,
      archivedCount: 0,
      // departments: [],
      loading: false,
    };
  },
  mounted() {
    this.loading = true;
    Promise.all([
      this.retrievecasefiles(this.$route.params.id),
      this.retrieveStages(),
    ]).finally(() => {
      this.loading = false;
    });
    // this.retrieveDepts(this.$route.params.id);
  },

  computed: {
    filteredCasefiles() {
      const user = this.$store.state.auth.user || {};
      const roles = Array.isArray(user.roles) ? user.roles : [];

      // ADMIN / MANAGER: full access
      if (roles.includes("MANAGER") || roles.includes("ADMIN")) {
        return this.casefiles;
      }

      // BRANCH-level users: restrict by branch only (no department required)
      if (roles.includes("BRANCHCLERK") || roles.includes("BRANCHMANAGER")) {
        const userBranches = Array.isArray(user.branches)
          ? user.branches
          : user.branches
          ? [user.branches]
          : [];
        if (userBranches.length === 0) {
          // If no branches assigned, return empty list (safe fallback)
          return [];
        }
        return this.casefiles.filter((cf) =>
          userBranches.some((b) => b === cf.branch),
        );
      }

      // ADJUSTER: keep existing tighter filter (dept + branch + adjuster)
      if (roles.includes("ADJUSTER")) {
        const userDepts = Array.isArray(user.dept) ? user.dept : [];
        const userBranches = Array.isArray(user.branches) ? user.branches : [];
        return this.casefiles.filter(
          (x) =>
            (userDepts.length === 0 || userDepts.includes(x.refType)) &&
            (userBranches.length === 0 || userBranches.includes(x.branch)) &&
            String(x.adjuster) === String(user.id),
        );
      }

      // EDITOR: keep existing tighter filter (dept + branch + editor)
      if (roles.includes("EDITOR")) {
        const userDepts = Array.isArray(user.dept) ? user.dept : [];
        const userBranches = Array.isArray(user.branches) ? user.branches : [];
        return this.casefiles.filter(
          (x) =>
            (userDepts.length === 0 || userDepts.includes(x.refType)) &&
            (userBranches.length === 0 || userBranches.includes(x.branch)) &&
            String(x.editor) === String(user.id),
        );
      }

      // Fallback: return all casefiles
      return this.casefiles;
    },
  },

  methods: {
    filterCase(item) {
      return this.filteredCasefiles.filter((a) => a.fileStatus === item);
    },
    countForStage(code) {
      if (
        this.countsByStatus &&
        Object.prototype.hasOwnProperty.call(this.countsByStatus, code)
      ) {
        return this.countsByStatus[code] || 0;
      }
      return this.filterCase(code).length;
    },
    async retrievecasefiles(id) {
      try {
        // Normalize branch id (accept numeric id or branch code/name)
        let branch = Number(id);
        if (Number.isNaN(branch)) {
          // try to resolve via paginated branches fetch
          try {
            // warm-up call (not used) — keep as a best-effort attempt to prime caches
            await CasefileDataService.getPaginated({
              page: 1,
              pageSize: 50,
              needCount: false,
            });
            // don't rely on casefile listing for branch lookup; instead fetch branches service if available
          } catch (e) {
            // ignore - primary goal is to resolve branch below using BranchDataService if needed
          }
          // fallback to fetching branches via BranchDataService
          try {
            const bResp = await (
              await import("../../services/BranchDataService")
            ).default.getAll();
            const branches = bResp.data || [];
            const found = branches.find(
              (b) =>
                String(b.brCode) === String(id) ||
                String(b.name) === String(id),
            );
            if (found) {
              branch = found.id;
            } else {
              branch = id; // leave as-is
            }
          } catch (be) {
            branch = id;
          }
        }

        const user = this.$store.state.auth.user || {};

        // Try paginated by branch first (prefer counts)
        let dataResp = null;
        try {
          dataResp = await CasefileDataService.getPaginatedByBranch(branch, {
            page: 1,
            limit: 50,
            needCount: true,
            roles: user.roles,
            dept: user.dept,
            branches: user.branches,
            userId: user.id,
          });
        } catch (e) {
          // fallback to legacy endpoint
          dataResp = await CasefileDataService.getAllBranch(branch);
        }

        if (dataResp) {
          if (Array.isArray(dataResp.data)) {
            this.casefiles = dataResp.data;
          } else if (dataResp.data && dataResp.data.data) {
            this.casefiles = dataResp.data.data;
            this.totalCount =
              dataResp.data.total ||
              dataResp.data.totalCount ||
              dataResp.data.data.length;
            // capture countsByStatus if provided
            if (dataResp.data.countsByStatus) {
              this.countsByStatus = dataResp.data.countsByStatus;
            }
          } else {
            this.casefiles = dataResp.data || [];
            this.totalCount = Array.isArray(this.casefiles)
              ? this.casefiles.length
              : 0;
          }
        }

        // Fetch Active/Archived breakdown for this branch
        try {
          const statusResp = await CasefileDataService.getCountsByStatus(
            "branch",
            {
              roles: user.roles,
              dept: user.dept,
              branches: user.branches,
              userId: user.id,
            },
          );
          if (statusResp.data && statusResp.data.counts) {
            const counts = statusResp.data.counts;
            this.activeCount = counts.active ? counts.active[branch] || 0 : 0;
            this.archivedCount = counts.archived
              ? counts.archived[branch] || 0
              : 0;
          }
        } catch (e) {
          console.error("Failed to fetch active/archived counts:", e);
        }
      } catch (e) {
        console.error("Failed to retrieve casefiles:", e);
      }
    },

    retrieveStages() {
      return StageDataService.getAll()
        .then((response) => {
          this.stages = response.data;
        })
        .catch((e) => {
          console.error(e);
        });
    },

    // retrieveDepts(id) {
    //   DeptDataService.get(id)
    //     .then((response) => {
    //       this.departments = response.data;
    //     })
    //     .catch((e) => {
    //       console.log(e);
    //     });
    // },

    viewItem(fileStatus, branch) {
      this.$router.push({
        name: "detail-by-branch",
        // params: { fileStatus: fileStatus },
        params: { fileStatus: fileStatus, branch: branch },
      });
    },
  },
};
</script>

<style scoped>
/* ...existing code... */
</style>
